import {
  Download,
  ExternalLink,
  Eye,
  FileCheck,
  FileText,
  X,
} from "lucide-react";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { formatDate } from "../data";
import type { KycDocument, KycRecord } from "@/types/kyc";

interface Props {
  record: KycRecord | null;
  open: boolean;
  onClose: () => void;
}

function resolveDocUrl(url?: string): string {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `http://localhost:5001${url.startsWith("/") ? "" : "/"}${url}`;
}

function isPdf(doc?: KycDocument | null): boolean {
  if (!doc) return false;
  const fileName = (doc.fileName || (doc as any).name || "").toLowerCase();
  const url = (doc.previewUrl || (doc as any).url || "").toLowerCase();
  return fileName.endsWith(".pdf") || url.includes(".pdf") || url.startsWith("data:application/pdf");
}

function getSafeDocProps(d: KycDocument) {
  const docType = d.docType || (d as any).type || "Document";
  const fileName = d.fileName || (d as any).name || "document";
  const fileSize = d.fileSize || (d as any).size || "";
  const previewUrl = resolveDocUrl(d.previewUrl || (d as any).url || "");
  return { docType, fileName, fileSize, previewUrl };
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-line py-2.5 last:border-b-0">
      <span className="shrink-0 text-[13px] text-ink-muted">{label}</span>
      <span className="text-right text-[13px] font-medium text-ink">{value || "\u2014"}</span>
    </div>
  );
}

function DocChip({ doc, onPreview }: { doc: KycDocument; onPreview: () => void }) {
  const { docType, fileName, fileSize } = getSafeDocProps(doc);

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-line bg-page px-3 py-2.5 transition-colors hover:border-slate-300">
      <FileText className="h-4 w-4 shrink-0 text-info" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-ink" title={fileName}>
          {fileName}
        </p>
        <p className="text-[11px] text-ink-muted">
          {docType} {fileSize ? `· ${fileSize}` : ""}
        </p>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-white hover:text-ink cursor-pointer"
        aria-label={`Preview ${fileName}`}
        title="Preview document"
      >
        <Eye className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function SubmittedDetailsModal({ record, open, onClose }: Props) {
  const [preview, setPreview] = useState<KycDocument | null>(null);

  if (!record) return null;

  const docs = Array.isArray(record.documents) ? record.documents : [];

  const businessDocs = docs.filter((d) => {
    const t = (d.docType || (d as any).type || "").toLowerCase();
    return (
      t.includes("pan") ||
      t.includes("gst") ||
      t.includes("incorporation") ||
      t.includes("udyam") ||
      t.includes("deed")
    );
  });

  const personalDocs = docs.filter((d) => {
    const t = (d.docType || (d as any).type || "").toLowerCase();
    return (
      t.includes("aadhaar") ||
      t.includes("selfie") ||
      t.includes("photo") ||
      t === "pan card" ||
      t.includes("personal pan")
    );
  });

  const bankDocs = docs.filter((d) => {
    const t = (d.docType || (d as any).type || "").toLowerCase();
    return t.includes("cancelled") || t.includes("cheque") || t.includes("bank") || t.includes("statement");
  });

  // Collect other documents that don't match standard buckets so nothing is ever lost
  const handledKeys = new Set([...businessDocs, ...personalDocs, ...bankDocs]);
  const otherDocs = docs.filter((d) => !handledKeys.has(d));

  const previewProps = preview ? getSafeDocProps(preview) : null;
  const isPdfFile = preview ? isPdf(preview) : false;

  return (
    <Modal
      open={open}
      onClose={() => {
        setPreview(null);
        onClose();
      }}
      title="Submitted Application Details"
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Document Preview Card */}
        {preview && previewProps && (
          <div className="relative flex flex-col gap-3 rounded-[10px] border border-info/40 bg-info-bg/30 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-info" />
                <span className="text-sm font-semibold text-ink">
                  Preview: {previewProps.fileName}
                </span>
                <span className="text-xs text-ink-muted">({previewProps.docType})</span>
              </div>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="rounded-md p-1 text-ink-muted hover:bg-slate-200 hover:text-ink cursor-pointer"
                title="Close preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {previewProps.previewUrl ? (
              isPdfFile ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-line bg-card p-6 text-center shadow-xs">
                  <FileText className="h-12 w-12 text-rose-500" />
                  <div>
                    <p className="text-sm font-medium text-ink">{previewProps.fileName}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">
                      PDF Document {previewProps.fileSize ? `· ${previewProps.fileSize}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={previewProps.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-9 items-center gap-2 rounded-md bg-primary-500 px-4 text-xs font-medium text-white hover:bg-primary-600 transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open PDF in New Tab
                    </a>
                    <a
                      href={previewProps.previewUrl}
                      download={previewProps.fileName}
                      className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-white px-3 text-xs font-medium text-ink hover:bg-slate-50 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </a>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-line bg-card p-3 shadow-xs">
                  <img
                    src={previewProps.previewUrl}
                    alt={previewProps.fileName}
                    className="max-h-[45vh] w-full rounded-md object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="flex w-full items-center justify-between px-1 pt-1">
                    <p className="text-xs text-ink-muted">
                      {previewProps.fileName} {previewProps.fileSize ? `· ${previewProps.fileSize}` : ""}
                    </p>
                    <a
                      href={previewProps.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:underline"
                    >
                      Open Full Image <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-line bg-card p-6 text-center">
                <FileText className="h-8 w-8 text-ink-muted" />
                <p className="mt-2 text-xs text-ink-secondary">
                  No preview URL available for this file ({previewProps.fileName}).
                </p>
              </div>
            )}
          </div>
        )}

        {/* 1. Business Details */}
        <div>
          <h4 className="text-sm font-semibold text-primary-500">1 · Business Details</h4>
          <div className="mt-2">
            <Row label="Legal Business Name" value={record.legalBusinessName} />
            <Row label="Trade or Brand Name" value={record.tradeName} />
            <Row label="Business Type" value={record.businessType} />
            <Row label="Business PAN" value={record.businessPan} />
            <Row label="GST Number" value={record.gstNumber} />
            <Row label="Year of Incorporation" value={record.yearOfIncorporation} />
            <Row label="Nature of Business" value={record.natureOfBusiness} />
            <Row label="Registered Address" value={record.registeredAddress} />
            <Row label="City" value={record.city} />
            <Row label="State" value={record.state} />
            <Row label="Pincode" value={record.pincode} />
            <Row label="Business Mobile" value={record.businessMobile} />
            <Row label="Business Email" value={record.businessEmail} />
          </div>
        </div>

        {/* 2. Business Documents */}
        <div>
          <h4 className="text-sm font-semibold text-primary-500">2 · Business Documents</h4>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {businessDocs.length > 0 ? (
              businessDocs.map((d, i) => (
                <DocChip
                  key={d.docType || (d as any).type || i}
                  doc={d}
                  onPreview={() => setPreview(d)}
                />
              ))
            ) : (
              <p className="col-span-2 text-xs text-ink-muted py-2">No business documents uploaded</p>
            )}
          </div>
        </div>

        {/* 3. Personal Documents */}
        <div>
          <h4 className="text-sm font-semibold text-primary-500">3 · Personal Documents</h4>
          <div className="mt-2">
            <Row label="Owner Full Name" value={record.ownerFullName} />
            <Row label="Date of Birth" value={record.dateOfBirth} />
            <Row label="Personal PAN" value={record.personalPan} />
            <Row label="Aadhaar Number" value={record.aadhaarNumber} />
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {personalDocs.length > 0 ? (
              personalDocs.map((d, i) => (
                <DocChip
                  key={d.docType || (d as any).type || i}
                  doc={d}
                  onPreview={() => setPreview(d)}
                />
              ))
            ) : (
              <p className="col-span-2 text-xs text-ink-muted py-2">No personal documents uploaded</p>
            )}
          </div>
        </div>

        {/* 4. Bank Details */}
        <div>
          <h4 className="text-sm font-semibold text-primary-500">4 · Bank Details</h4>
          <div className="mt-2">
            <Row label="Account Holder Name" value={record.accountHolderName} />
            <Row label="Account Number" value={record.accountNumber} />
            <Row label="IFSC Code" value={record.ifsc || (record as any).ifscCode} />
            <Row label="Bank Name" value={record.bankName} />
            <Row label="Account Type" value={record.accountType} />
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {bankDocs.length > 0 ? (
              bankDocs.map((d, i) => (
                <DocChip
                  key={d.docType || (d as any).type || i}
                  doc={d}
                  onPreview={() => setPreview(d)}
                />
              ))
            ) : (
              <p className="col-span-2 text-xs text-ink-muted py-2">
                No bank proof document uploaded (optional)
              </p>
            )}
          </div>
        </div>

        {/* Other Uploaded Documents if any */}
        {otherDocs.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-primary-500">Other Documents</h4>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {otherDocs.map((d, i) => (
                <DocChip
                  key={d.docType || (d as any).type || i}
                  doc={d}
                  onPreview={() => setPreview(d)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Application Details */}
        <div>
          <h4 className="text-sm font-semibold text-primary-500">Application</h4>
          <div className="mt-2">
            <Row label="Application ID" value={record.applicationId || record.id} />
            <Row
              label="Submitted On"
              value={record.submittedAt ? formatDate(record.submittedAt) : "—"}
            />
            <Row
              label="Reviewed On"
              value={record.reviewedAt ? formatDate(record.reviewedAt) : "—"}
            />
            <Row label="Reviewed By" value={record.reviewedBy ?? "—"} />
            {record.rejectionReason && (
              <Row label="Rejection Reason" value={record.rejectionReason} />
            )}
            {record.remarks && <Row label="Remarks" value={record.remarks} />}
          </div>
        </div>
      </div>
    </Modal>
  );
}